import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from '@/lib/api';
import { applyLanguage } from "@/lib/applyLanguage";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const languages = [
  { code: "pt", name: "Português", flagCode: "pt" },
  { code: "en", name: "English", flagCode: "gb" },
  { code: "es", name: "Español", flagCode: "es" },
  { code: "fr", name: "Français", flagCode: "fr" },
];

export function LanguageSwitcher({ variant = "ghost", size = "sm", className = "", noTooltip = false, stacked = false, stackedLabel = "", testId = "language-switcher" }) {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const suppressRef = useRef(false);

  const handleTooltipChange = useCallback((open) => {
    if (dropdownOpen || suppressRef.current) {
      setTooltipOpen(false);
      return;
    }
    setTooltipOpen(open);
  }, [dropdownOpen]);

  const handleDropdownChange = useCallback((open) => {
    setDropdownOpen(open);
    if (open) setTooltipOpen(false);
  }, []);

  const handleLanguageChange = async (langCode) => {
    if (langCode === i18n.language) return;

    // Suppress tooltip
    suppressRef.current = true;
    setTooltipOpen(false);
    setTimeout(() => { suppressRef.current = false; }, 800);

    // Switch language, mark it as an explicit choice, and reflect it in the
    // URL via ?lng= (pt = clean URL) so shared links open in the same language.
    applyLanguage(i18n, langCode);

    if (user) {
      try {
        await api.put("/api/preferences", { preferred_language: langCode });
      } catch (error) {
        console.error("Error saving language preference:", error);
      }
    }
  };

  const triggerButton = (
    <DropdownMenuTrigger asChild>
      {stacked ? (
        <button
          type="button"
          className={`flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-muted text-foreground min-w-[58px] ${className}`}
          data-testid={testId}
          title={noTooltip ? t("header.tooltip_language") : undefined}
        >
          <img loading="lazy"
            src={`https://flagcdn.com/16x12/${currentLang.flagCode}.png`}
            srcSet={`https://flagcdn.com/32x24/${currentLang.flagCode}.png 2x`}
            alt={currentLang.code.toUpperCase()}
            className="w-5 h-[15px]"
          />
          <span className="text-[10px] leading-tight text-center">{stackedLabel || currentLang.code.toUpperCase()}</span>
        </button>
      ) : (
        <Button
          variant={variant}
          size={size}
          className={`gap-1.5 focus-visible:ring-0 focus-visible:ring-offset-0 ${className}`}
          data-testid={testId}
          title={noTooltip ? t("header.tooltip_language") : undefined}
        >
          <img loading="lazy"
            src={`https://flagcdn.com/16x12/${currentLang.flagCode}.png`}
            srcSet={`https://flagcdn.com/32x24/${currentLang.flagCode}.png 2x`}
            alt={currentLang.code.toUpperCase()}
            className="w-4 h-3"
          />
          <span className="text-sm font-medium hidden md:inline">{currentLang.code.toUpperCase()}</span>
        </Button>
      )}
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={handleDropdownChange}>
      {noTooltip ? triggerButton : (
        <TooltipProvider>
          <Tooltip open={tooltipOpen} onOpenChange={handleTooltipChange} delayDuration={300}>
            <TooltipTrigger asChild>
              {triggerButton}
            </TooltipTrigger>
            <TooltipContent side="bottom" className="z-[10000]">
              <p>{t("header.tooltip_language")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <DropdownMenuContent align="end" sideOffset={8} className="min-w-[150px] z-[10000]">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`cursor-pointer gap-2 ${
              i18n.language === lang.code ? "bg-accent" : ""
            }`}
            data-testid={`lang-option-${lang.code}`}
          >
            <img loading="lazy" 
              src={`https://flagcdn.com/16x12/${lang.flagCode}.png`}
              srcSet={`https://flagcdn.com/32x24/${lang.flagCode}.png 2x`}
              alt={lang.code.toUpperCase()}
              className="w-4 h-3"
            />
            <span className="font-medium">{lang.code.toUpperCase()}</span>
            <span className="text-muted-foreground">—</span>
            <span>{lang.name}</span>
            {i18n.language === lang.code && (
              <span className="ml-auto text-primary">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSwitcher;
